<template>
    <div>
        <v-data-table
            :headers="headers"
            :items="invoicesFiltered"
            class="elevation-0"
            :mobile-breakpoint="0"
            :loading="loading"
            disable-pagination
            hide-default-footer
            fixed-header
        >
            <template v-slot:top>
                <v-row
                    no-gutter
                    class="mt-0 mx-0 pt-4 pb-0 pb-4"
                    :style="{ 'background-color': '#eeeeee' }"
                >
                    <v-col cols="12" class="d-flex mb-n3">
                        <h1 class="mr-4">GENERATING</h1>
                        <v-btn
                            v-if="readAllInvoices"
                            color="primary"
                            rounded
                            class="mx-2"
                            @click="openFindInvoice"
                        >
                            <v-icon class="mx-1">mdi-database-search</v-icon>
                            FIND
                        </v-btn>
                    </v-col>
                </v-row>
            </template>
            <!--HEADERS-->
            <template v-slot:[`header.number`]="{ header }">
                <v-text-field
                    class="py-2 mt-2"
                    :label="header.text"
                    v-model="codeToFilter"
                    dense
                />
            </template>
            <template v-slot:[`header.createdBy`]="{ header }">
                <v-text-field
                    class="py-2 mt-2"
                    :label="header.text"
                    v-model="userToFilter"
                    dense
                />
            </template>
            <template v-slot:[`header.project`]="{ header }">
                <v-text-field
                    class="py-2 mt-2"
                    :label="header.text"
                    v-model="projectToFilter"
                    dense
                />
            </template>
            <template v-slot:[`header.region`]="{ header }">
                <v-text-field
                    class="py-2 mt-2"
                    :label="header.text"
                    v-model="regionToFilter"
                    dense
                />
            </template>
            <template v-slot:[`item.status`]="{ item }">
                <div class="d-flex justify-center">
                    <v-chip
                        :color="statusColor[item.status]"
                        class="white--text text-capitalize"
                    >
                        {{ item.status }}
                    </v-chip>
                </div>
            </template>
            <!--ITEMS-->
            <template v-slot:[`item.createdOn`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">
                        {{ formatDate(item.createdOn._seconds) }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.createdBy`]="{ item }">
                <div class="d-flex justify-center">
                    <p v-if="item.createdBy" class="my-0 text-capitalize">
                        {{ item.createdBy.name }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.project`]="{ item }">
                <div class="d-flex justify-center">
                    <p v-if="item.project" class="my-0">
                        {{ item.project.name }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.client`]="{ item }">
                <div class="d-flex justify-center">
                    <p v-if="item.client" class="my-0">
                        {{ item.client.name }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.currency`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">{{ item.currency }}</p>
                </div>
            </template>
            <template v-slot:[`item.amount`]="{ item }">
                <div class="d-flex justify-end align-right">
                    <p class="my-0">
                        {{ numberFormat(item) }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.billed`]="{ item }">
                <div class="d-flex justify-end align-right">
                    <p class="my-0">
                        {{
                            item.type == 'proforma'
                                ? numberFormat(item)
                                : billedFormat(item)
                        }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.region`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="text-capitalize my-0">
                        {{ item.region }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
                <div class="d-flex justify-center">
                    <v-btn small icon @click="openInvoiceProcess(item)">
                        <v-icon>mdi-eye</v-icon>
                    </v-btn>
                </div>
            </template>
        </v-data-table>
        <v-alert
            v-if="activateAlert"
            type="success"
            color="primary"
            :style="{ position: 'absolute', right: '0px', bottom: '0px' }"
        >
            <v-row no-gutters>
                <v-col cols="11">
                    <p class="my-0">{{ alertMessage }}</p>
                </v-col>
                <v-col cols="1">
                    <v-btn
                        small
                        icon
                        class="mt-n1"
                        @click="activateAlert = false"
                    >
                        <v-icon>
                            mdi-close
                        </v-icon>
                    </v-btn>
                </v-col>
            </v-row>
        </v-alert>
        <!--INVOICE DETAILS-->
        <v-dialog
            v-model="invoiceProcess"
            :retain-focus="false"
            persistent
            fullscreen
        >
            <GenerateInvoice
                v-if="invoiceProcess"
                :invoice="selectedInvoice"
                :project="selectedInvoice.project"
                :setting="invoiceSettings"
                @close="closeInvoiceProcess"
                @generate="generate"
                @removeInvoice="removeInvoice"
                @replaceInvoice="replaceInvoice"
            />
        </v-dialog>
        <!--INVOICE DETAILS-->
        <v-dialog
            v-model="creditNoteProcess"
            :retain-focus="false"
            persistent
            fullscreen
        >
            <CreditNoteRequest
                v-if="creditNoteProcess && selectedInvoice.type != 'proforma'"
                :invoice="selectedInvoice"
                :project="selectedInvoice.project"
                :setting="invoiceSettings"
                @close="closeCreditNoteProcess"
                @generate="generate"
                @removeInvoice="removeInvoice"
                @replaceInvoice="replaceInvoice"
            />

            <CreditNoteRequestProforma
                v-if="creditNoteProcess && selectedInvoice.type == 'proforma'"
                :invoice="selectedInvoice"
                :project="selectedInvoice.project"
                @close="closeCreditNoteProcess"
                @generate="generate"
                @removeInvoice="removeInvoice"
                @replaceInvoice="replaceInvoice"
            />
        </v-dialog>
        <!--PROFORMA DETAILS-->
        <v-dialog
            v-model="proformaDialog"
            :retain-focus="false"
            persistent
            fullscreen
        >
            <ProformaFeaturesGenerating
                v-if="proformaDialog"
                :invoice="selectedInvoice"
                :project="selectedInvoice.project || {}"
                @close="closeInvoiceProcess"
                @generate="generate"
                @removeInvoice="removeInvoice"
                @replaceInvoice="replaceInvoice"
            />
        </v-dialog>
        <!-- FIND INVOICE -->
        <v-dialog
            v-model="findInvoice"
            :retain-focus="false"
            persistent
            max-width="600"
        >
            <FindInvoice
                v-if="findInvoice"
                :status="['generating', 'credit']"
                :projects="projects"
                :clients="clients"
                @close="closeFindInvoice"
                @openInvoice="openInvoiceProcess"
            />
        </v-dialog>
    </div>
</template>

<script>
import moment from 'moment'
import _ from 'lodash'
import API from '@/services/api'
import { mapMutations, mapGetters, mapState, mapActions } from 'vuex'

export default {
    name: 'Generating',
    props: {
        setting: {
            type: Object,
            default: () => ({}),
        },
    },
    components: {
        FindInvoice: () => import('@/components/Invoicing/FindInvoice.vue'),
        GenerateInvoice: () =>
            import('@/components/Invoicing/GenerateInvoice.vue'),
        ProformaFeaturesGenerating: () =>
            import('@/components/Invoicing/ProformaFeaturesGenerating.vue'),
        CreditNoteRequest: () =>
            import('@/components/Invoicing/CreditNoteRequest.vue'),
        CreditNoteRequestProforma: () =>
            import('@/components/Invoicing/CreditNoteRequestProforma.vue'),
    },
    data: () => ({
        proformaDialog: false,
        selectedInvoice: undefined,
        invoiceProcess: false,
        sendInvoice: false,
        codeToFilter: undefined,
        userToFilter: undefined,
        projectToFilter: undefined,
        regionToFilter: undefined,
        loading: false,
        invoices: [],
        activateAlert: false,
        alertMessage: undefined,
        headers: [
            {
                text: 'ID',
                value: 'number',
                align: 'center',
                sortable: false,
            },
            {
                text: 'CREATED ON',
                value: 'createdOn',
                align: 'center',
                sortable: false,
            },
            {
                text: 'CREATED BY',
                value: 'createdBy',
                align: 'center',
                sortable: false,
            },
            {
                text: 'PROJECT',
                value: 'project',
                align: 'center',
                sortable: false,
            },
            {
                text: 'CLIENT',
                value: 'client',
                align: 'center',
                sortable: false,
            },
            {
                text: 'CURRENCY',
                value: 'currency',
                align: 'center',
                sortable: false,
            },
            {
                text: 'PRICE',
                value: 'amount',
                align: 'center',
                sortable: false,
            },
            {
                text: 'AMOUNT',
                value: 'billed',
                align: 'center',
                sortable: false,
            },
            {
                text: 'REGION',
                value: 'region',
                align: 'center',
                sortable: false,
            },
            {
                text: 'STATUS',
                value: 'status',
                align: 'center',
                sortable: false,
            },
            {
                text: 'ACTIONS',
                value: 'actions',
                align: 'center',
                sortable: false,
            },
        ],
        companyId: JSON.parse(localStorage.getItem('company')),
        projects: [],
        users: [],
        clients: [],
        invoiceSettings: {},
        resourceId: undefined,
        statusColor: {
            generating: '#edc43b',
            closed: '#000000',
            credit: '#eda73b',
        },
        creditNoteProcess: false,
        findInvoice: false,
        readAllInvoices: false,
    }),
    computed: {
        ...mapState(['notificationResource']),
        ...mapState(['invoiceUpdates']),
        invoicesFiltered() {
            this.openResource()
            let conditions = []
            if (this.codeToFilter) {
                conditions.push(this.filterCode)
            }
            if (this.userToFilter) {
                conditions.push(this.filterUser)
            }
            if (this.projectToFilter) {
                conditions.push(this.filterProject)
            }
            if (this.regionToFilter) {
                conditions.push(this.filterRegion)
            }
            if (conditions.length > 0) {
                return this.invoices.filter(invoice => {
                    return conditions.every(condition => {
                        return condition(invoice)
                    })
                })
            }
            this.sort()
            return this.invoices
        },
    },
    watch: {
        notificationResource: function(resource) {
            if (resource) {
                this.resourceId = resource
                this.openResource()
                this.setNotificationResource(undefined)
            }
        },
        invoiceUpdates: function(data) {
            if (data) {
                this.UpdateInvoice(data)
                this.setInvoiceUpdates(undefined)
            }
        },
    },
    async mounted() {
        try {
            this.loading = true
            this.resourceId = this.$route.query.resourceId
            // get user
            const {
                data: { user },
            } = await API.getMyInfo()
            // set permission
            this.readAllInvoices = user.permissions.includes('readAllInvoices')
            await this.getSetting()
            await this.loadData()
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        ...mapActions(['setNotificationResource']),
        ...mapGetters(['getNotificationResource']),
        ...mapActions(['setInvoiceUpdates']),
        ...mapGetters(['getInvoiceUpdates']),
        replaceInvoice(invoice) {
            const index = this.invoices.findIndex(i => i.id == invoice.id)
            if (index >= 0) {
                if (
                    invoice.status != 'credit' ||
                    invoice.status != 'generating'
                ) {
                    this.invoices.splice(index, 1)
                } else {
                    this.invoices.splice(index, 1, {
                        ...this.invoices[index],
                        ...invoice,
                    })
                }
            }
        },
        removeInvoice(invoice) {
            this.invoices = this.invoices.filter(i => i.id != invoice.id)
            this.closeInvoiceProcess()
        },
        async getSetting() {
            try {
                this.loading = true
                if (Object.keys(this.setting).length > 0) {
                    this.invoiceSettings = this.setting
                } else {
                    const {
                        data: { settings },
                    } = await API.getSettings()
                    const setting = settings.find(
                        setting => setting.name.toLowerCase() == 'invoicing'
                    )
                    if (setting) {
                        this.invoiceSettings = setting
                    }
                }
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        generate(invoice) {
            this.invoices = this.invoices.filter(i => i.id != invoice.id)
            this.activateAlert = true
            this.alertMessage = 'Invoice Request was successfully generated.'
        },
        async loadData() {
            try {
                this.loading = true
                const invoices = await API.getInvoices({
                    status: 'generating',
                })
                this.projects = await API.getLiteProjects({ all: true })
                const {
                    data: { users },
                } = await API.getLiteUsers()
                const {
                    data: { clients },
                } = await API.getLiteClients()
                this.clients = clients
                this.users = users
                for (let invoice of invoices) {
                    //set client
                    invoice.client = this.clients.find(
                        client => client.id == invoice.clientId
                    )
                    //set createdBy
                    const userIndex = this.users.findIndex(
                        user => user.id == invoice.createdBy
                    )
                    if (userIndex > -1) {
                        invoice.createdBy = this.users[userIndex]
                    }
                    //set Project
                    const projectIndex = this.projects.findIndex(
                        project => project.id == invoice.projectId
                    )
                    if (projectIndex > -1) {
                        invoice.project = this.projects[projectIndex]
                    }
                    this.invoices.push(invoice)
                }
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        setInvoiceData(invoice) {
            //set client
            invoice.client = this.clients.find(
                client => client.id == invoice.clientId
            )
            //set createdBy
            const userIndex = this.users.findIndex(
                user => user.id == invoice.createdBy
            )
            if (userIndex > -1) {
                invoice.createdBy = this.users[userIndex]
            }
            //set Project
            const projectIndex = this.projects.findIndex(
                project => project.id == invoice.projectId
            )
            if (projectIndex > -1) {
                invoice.project = this.projects[projectIndex]
            }
            return invoice
        },
        async openInvoiceProcess(item) {
            try {
                if (!item.project) {
                    item = this.setInvoiceData(item)
                }
                this.selectedInvoice = _.cloneDeep(item)
                if (
                    this.selectedInvoice.type == 'proforma' &&
                    this.selectedInvoice.status != 'credit'
                ) {
                    this.proformaDialog = true
                } else if (
                    this.selectedInvoice.status == 'credit' ||
                    (this.selectedInvoice.type == 'proforma' &&
                        this.selectedInvoice.status == 'credit')
                ) {
                    if (this.selectedInvoice.project) {
                        this.selectedInvoice.project.quotes = await API.getProjectQuotes(
                            this.selectedInvoice.project.id
                        )
                    }
                    this.creditNoteProcess = true
                } else {
                    if (this.selectedInvoice.project) {
                        this.selectedInvoice.project.quotes = await API.getProjectQuotes(
                            this.selectedInvoice.project.id
                        )
                    }
                    this.invoiceProcess = true
                }
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
        closeInvoiceProcess() {
            this.selectedInvoice = {}
            this.invoiceProcess = false
            this.proformaDialog = false
        },
        closeCreditNoteProcess() {
            this.selectedInvoice = {}
            this.creditNoteProcess = false
            this.proformaDialog = false
        },
        closeSendDialog() {
            this.selectedInvoice = {}
            this.sendInvoice = false
        },
        filterCode(invoice) {
            let number = invoice.number
            if (typeof invoice.number == 'number') {
                number = invoice.number.toString()
            }
            return number
                .toLowerCase()
                .includes(this.codeToFilter.toLowerCase())
        },
        filterUser(invoice) {
            if (invoice.createdBy.name) {
                return invoice.createdBy.name
                    .toLowerCase()
                    .includes(this.userToFilter.toLowerCase())
            }
        },
        filterProject(invoice) {
            if (invoice.project) {
                return (
                    invoice.project.name
                        .toLowerCase()
                        .includes(this.projectToFilter.toLowerCase()) ||
                    invoice.project.reference
                        .toString()
                        .toLowerCase()
                        .includes(this.projectToFilter.toLowerCase())
                )
            }
        },
        filterRegion(invoice) {
            return invoice.region
                .toLowerCase()
                .includes(this.regionToFilter.toLowerCase())
        },
        formatDate(seconds) {
            return moment.unix(seconds).format('L LT')
        },
        numberFormat(invoice) {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: invoice.currency,
                maximumFractionDigits: invoice.currency == 'USD' ? 2 : 0,
            }).format(
                invoice.currency == 'USD'
                    ? invoice.amount
                    : Math.ceil(invoice.amount)
            )
        },
        billedFormat(invoice) {
            let invoiceAmount = 0
            for (let key in invoice.items) {
                for (let cost of invoice.items[key]) {
                    if (cost.currentInvoice) {
                        invoiceAmount += cost.currentInvoice
                    } else if (
                        cost.invoiceQuantity &&
                        cost.pricePerUnit &&
                        Number(cost.invoiceQuantity) > 0 &&
                        Number(cost.pricePerUnit) > 0
                    ) {
                        invoiceAmount +=
                            Number(cost.invoiceQuantity) *
                            Number(cost.pricePerUnit)
                    }
                }
            }
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: invoice.currency,
                maximumFractionDigits: invoice.currency == 'USD' ? 2 : 0,
            }).format(
                invoice.currency == 'USD'
                    ? invoiceAmount
                    : Math.ceil(invoiceAmount)
            )
        },
        sort() {
            this.invoices.sort(
                (a, b) =>
                    (b.createdOn.seconds || b.createdOn._seconds) -
                    (a.createdOn.seconds || a.createdOn._seconds)
            )
        },
        openResource() {
            if (this.resourceId) {
                const resource = this.invoices.find(
                    i => i.id == this.resourceId
                )
                if (resource) {
                    this.resourceId = undefined
                    this.openInvoiceProcess(resource)
                }
            }
        },
        async UpdateInvoice(invoice) {
            try {
                const index = this.invoices.findIndex(i => i.id == invoice.id)
                if (index >= 0) {
                    if (
                        invoice.status == 'generating' ||
                        invoice.status == 'credit'
                    ) {
                        const updatedInvoice = {
                            ...this.invoices[index],
                            ...invoice,
                        }
                        this.invoices.splice(index, 1, updatedInvoice)
                    } else {
                        this.invoices = this.invoices.filter(
                            i => i.id != invoice.id
                        )
                    }
                } else if (
                    invoice.status == 'generating' ||
                    invoice.status == 'credit'
                ) {
                    //set client
                    invoice.client = this.clients.find(
                        client => client.id == invoice.clientId
                    )
                    //set createdBy
                    const userIndex = this.users.findIndex(
                        user => user.id == invoice.createdBy
                    )
                    if (userIndex > -1) {
                        invoice.createdBy = this.users[userIndex]
                    }
                    //set Project
                    const projectIndex = this.projects.findIndex(
                        project => project.id == invoice.projectId
                    )
                    if (projectIndex > -1) {
                        invoice.project = this.projects[projectIndex]
                    }
                    this.invoices.push(invoice)
                }
            } catch (error) {
                console.error(error)
            }
        },
        closeFindInvoice() {
            this.findInvoice = false
        },
        openFindInvoice() {
            this.findInvoice = true
        },
    },
}
</script>

<style>
.v-input .v-label {
    font-size: 12px;
}
.v-data-table-header th {
    background-color: #eeeeee !important;
}
.v-data-table__wrapper {
    max-height: 72vh;
    overflow-y: auto;
}
.v-data-table__row {
    height: 64px;
}
.v-data-table__wrapper thead tr {
    position: sticky;
    top: 0;
    z-index: 10;
}
thead {
    position: sticky;
    z-index: 1;
}
</style>
